<template>
    <div>
        <el-form data-cy="user-form" v-if="USER_PERMISSIONS && formFieldsRoute" label-width="120px" :rules="rules" :model="formFieldsRoute" ref="form" label-position="top" v-loading="loading">
            <h1 v-if="formFieldsRoute.id">Edit User</h1>
            <h1 v-else>New User</h1>

            <!-- Role Override Banner -->
            <div v-if="overrideCount > 0" class="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mt-4 text-sm text-yellow-800">
                You have overridden {{ overrideCount }} permission(s) that were locked by this user's roles.
            </div>

            <!-- User Info -->
            <div class="card mt-4">
                <div class="card-title">User Info</div>
                <div>
                    <div class="flex flex-wrap gap-4">
                        <el-form-item class="flex-grow" label="First Name" prop="firstName" required>
                            <el-input data-cy="firstName" v-model="formFieldsRoute.firstName" tabindex="1"></el-input>
                        </el-form-item>
                        <el-form-item class="flex-grow" label="Last Name" prop="lastName" required>
                            <el-input data-cy="lastName" v-model="formFieldsRoute.lastName" tabindex="2"></el-input>
                        </el-form-item>
                    </div>
                    <div class="flex flex-wrap gap-4">
                        <el-form-item class="flex-grow" label="Email" prop="email">
                            <el-input data-cy="email" v-model.trim="formFieldsRoute.email" tabindex="3"></el-input>
                        </el-form-item>
                        <el-form-item class="flex-grow" label="Phone" prop="phone" required>
                            <InputPhoneNumber data-cy="phone" v-model="formFieldsRoute.phone"/>
                        </el-form-item>
                        <el-form-item v-if="!$store.getters.isZohoCrmDisabled" class="flex-grow" v-show="$store.getters.isSystemAdmin" label="Zoho CRM Contact ID" prop="zohoCrmContactRecordId">
                            <el-input v-model="formFieldsRoute.zohoCrmContactRecordId" disabled></el-input>
                        </el-form-item>
                    </div>
                    <div class="flex flex-wrap gap-4">
                        <el-form-item class="flex-grow" label="Reports To">
                            <el-select v-model="formFieldsRoute.reportsToUserId" placeholder="Select manager" clearable filterable :popper-append-to-body="false">
                                <el-option
                                    v-for="user in availableManagers"
                                    :key="user.id"
                                    :label="`${user.firstName} ${user.lastName}`"
                                    :value="user.id">
                                </el-option>
                            </el-select>
                        </el-form-item>
                        <el-form-item class="flex-grow" :label="`Linked ${$t('label.associate')}`">
                            <el-select v-model="formFieldsRoute.samePersonAsStaffId" placeholder="Select associate" clearable filterable :popper-append-to-body="false">
                                <el-option
                                    v-for="staff in availableStaff"
                                    :key="staff.id"
                                    :label="`${staff.firstName} ${staff.lastName}`"
                                    :value="staff.id">
                                </el-option>
                            </el-select>
                        </el-form-item>
                    </div>
                    <div v-if="tenantRoles.length > 0" class="flex flex-wrap gap-4">
                        <el-form-item class="flex-grow" label="Roles">
                            <el-select
                                v-model="assignedRoleIds"
                                multiple
                                placeholder="Select roles"
                                @change="formFieldsRoute.id ? handleRoleChange($event) : null"
                            >
                                <el-option
                                    v-for="role in tenantRoles"
                                    :key="role.id"
                                    :label="role.name"
                                    :value="role.id"
                                />
                            </el-select>
                        </el-form-item>
                    </div>
                </div>
            </div>

            <!-- Direct Reports -->
            <div class="card" v-if="formFieldsRoute.id && directReports.length">
                <div class="card-title">Direct Reports</div>
                <div class="card-subtitle">Users who report directly to this user.</div>
                <el-table :data="directReports" size="small" class="w-full">
                    <el-table-column label="Name" prop="name">
                        <template slot-scope="scope">
                            {{ scope.row.firstName }} {{ scope.row.lastName }}
                        </template>
                    </el-table-column>
                    <el-table-column label="Email" prop="email" />
                </el-table>
            </div>

            <!-- Notifications -->
            <!--
            <div class="card">        
                <div class="card-title">Notifications</div>
                <div class="card-subtitle">Choose which notifications this user receives via SMS and email. In-App notifications cannot be disabled.</div>
                <div class="grid grid-cols-12 grid-flow-rows gap-4 items-center justify-center font-medium">
                    <div class=" hidden md:block md:col-span-6 lg:col-span-9 2xl:col-span-9"></div>
                    <div class=" hidden md:block md:col-span-2 lg:col-span-1 2xl:col-span-1">In-App</div>
                    <div class=" hidden md:block md:col-span-2 lg:col-span-1 2xl:col-span-1">SMS</div>
                    <div class=" hidden md:block md:col-span-2 lg:col-span-1 2xl:col-span-1">Email</div>
                </div>
                <div class="grid grid-cols-12 grid-flow-rows gap-4 border-b last:border-b-0 border-gray-300 py-4 items-center" v-for="option in notificationOptions" :key="option.title">
                    <div class="col-span-12 md:col-span-6 lg:col-span-9 2xl:col-span-9 break-normal">
                        <div class="text-black">{{option.title}}</div>
                        <div class="font-medium text-gray-600 text-sm">{{option.description}}</div>
                    </div>
                    <div class="col-span-12 md:col-span-2 lg:col-span-1 2xl:col-span-1">
                        <span class="block md:hidden">In-App</span><el-switch disabled :value="true"></el-switch>
                    </div>
                    <div class="col-span-12 md:col-span-2 lg:col-span-1 2xl:col-span-1">
                        <span class="block md:hidden">SMS</span><el-switch v-model="formFieldsRoute[option.smsField]"></el-switch>
                    </div>
                    <div class="col-span-12 md:col-span-2 lg:col-span-1 2xl:col-span-1">
                        <span class="block md:hidden">Email</span>
                        <el-tooltip class="item" effect="dark" placement="top-start" width="100px"
                            v-if="isEmailDisabled"
                            content="The User has unsubscribed or marked your company's emails as spam. Please contact Hera Support to resolve this issue.">
                            <el-switch disabled></el-switch>
                        </el-tooltip>
                        <el-switch v-else v-model="formFieldsRoute[option.emailField]"></el-switch>
                    </div>
                </div>
            </div>
            -->

            <!-- Account -->
            <div class="card">    
                <div class="card-title">Account</div>
                <div class="card-subtitle">Control login and admin access for this user.</div>

                <div data-cy="uf-account-control-access-item" class="grid grid-cols-12 gap-4 border-b last:border-b-0 border-gray-300 py-4 items-center" v-for="option in accountOptions" :key="option.key">
                    <div class="col-span-12 md:col-span-10 lg:col-span-11 2xl:col-span-11 break-normal">
                        <div class="text-black">
                            {{option.title}}
                            <span v-if="locksByPermissionKey[option.field]" class="text-xs font-medium ml-2" :class="locksByPermissionKey[option.field].isOverridden ? 'text-yellow-600' : 'text-blue-600'">
                                {{ locksByPermissionKey[option.field].isOverridden ? 'Override active' : getLockedByLabel(option.field) }}
                            </span>
                        </div>
                        <div class="font-medium text-gray-600 text-sm pr-8">{{option.description}}</div>
                        <div v-if="locksByPermissionKey[option.field]" class="mt-1">
                            <el-switch
                                :value="locksByPermissionKey[option.field].isOverridden"
                                @change="toggleOverride(option.field)"
                                active-text="Override Role Lock"
                                size="mini"
                            />
                        </div>
                    </div>
                    <div class="col-span-12 md:col-span-2 lg:col-span-1 2xl:col-span-1" >
                        <el-switch :id="option.key" @change="updateToggles(option.field)" :disabled="(!formFieldsRoute.permissionLogin && option.field != 'permissionLogin') || isPermissionLocked(option.field)" v-model="formFieldsRoute[option.field]"></el-switch>
                    </div>
                </div>
            </div>

            <!-- Permissions -->
            <div class="card" v-if="!formFieldsRoute.permissionFullAccess" :class="{'opacity-50': !formFieldsRoute.permissionLogin}">
                <div class="card-title">Permissions</div>
                <div class="card-subtitle">Customize individual permissions for this user.</div>

                <div data-cy="uf-permission-item" class="grid grid-cols-12 gap-4 border-b last:border-b-0 py-4 items-center" v-for="option in permissionOptions" :key="option.key">
                    <div class="col-span-12 md:col-span-10 lg:col-span-11 2xl:col-span-11 break-normal">
                        <div class="text-black">
                            {{option.title}}
                            <span v-if="locksByPermissionKey[option.field]" class="text-xs font-medium ml-2" :class="locksByPermissionKey[option.field].isOverridden ? 'text-yellow-600' : 'text-blue-600'">
                                {{ locksByPermissionKey[option.field].isOverridden ? 'Override active' : getLockedByLabel(option.field) }}
                            </span>
                        </div>
                        <div class="font-medium text-gray-600 text-sm pr-8">{{option.description}}</div>
                        <div v-if="option.scopeField && formFieldsRoute[option.field]" class="mt-2">
                            <span class="text-gray-600 text-xs mr-2">Visibility:</span>
                            <el-select v-model="formFieldsRoute[option.scopeField]" size="mini" style="width: 200px" placeholder="Scope" :disabled="isPermissionLocked(option.field)">
                                <el-option
                                    v-for="scope in scopeOptions"
                                    :key="scope.value"
                                    :label="scope.label"
                                    :value="scope.value">
                                </el-option>
                            </el-select>
                        </div>
                        <div v-if="locksByPermissionKey[option.field]" class="mt-1">
                            <el-switch
                                :value="locksByPermissionKey[option.field].isOverridden"
                                @change="toggleOverride(option.field)"
                                active-text="Override Role Lock"
                                size="mini"
                            />
                        </div>
                    </div>
                    <div class="col-span-12 md:col-span-2 lg:col-span-1 2xl:col-span-1" >
                        <el-switch :id="option.key" @change="updateToggles(option.field)" :disabled="!formFieldsRoute.permissionLogin || isPermissionLocked(option.field) || (option.field === 'permissionDeleteCounselings' && !formFieldsRoute.permissionManageCounselings)" v-model="formFieldsRoute[option.field]"></el-switch>
                    </div>
                </div>
            </div>

            <div v-if="!formFieldsRoute.id" class=" text-right mt-8 -mb-4 text-sm font-medium text-gray-600">
                Hera will email this user a temporary password to get started.
            </div>
            <div class=" text-right mt-8">
                <ButtonGroupWrapper>
                    <template slot-scope="{disabled, setDisabled}">
                        <el-button
                            :disabled="disabled || loading"
                            @click="$router.go(-1)"
                        >
                            Cancel
                        </el-button>
                        <ButtonWrapper
                            v-if="formFieldsRoute.id"
                            type="primary"
                            data-cy="update-button"
                            :clickHandler="confirmUpdateUser"
                            :disabled="disabled || loading"
                            @cb-start="setDisabled(true)"
                            @cb-end="setDisabled(false)"
                        >
                            Update
                        </ButtonWrapper>
                        <ButtonWrapper
                            v-else
                            type="primary"
                            :clickHandler="createUser"
                            :disabled="disabled || loading"
                            @cb-start="setDisabled(true)"
                            @cb-end="setDisabled(false)"
                        >
                            Create
                        </ButtonWrapper>
                    </template>
                </ButtonGroupWrapper>
          </div>
        </el-form>

        <el-form v-else-if="formFields" label-width="120px" :rules="rules" :model="formFields" ref="form" >
            <el-form-item label="First Name" prop="firstName" required>
                <el-input v-model="formFields.firstName"></el-input>
            </el-form-item>
            <el-form-item label="Last Name" prop="lastName" required>
                <el-input v-model="formFields.lastName"></el-input>
            </el-form-item>
            <el-form-item label="Phone" prop="phone" required>
                <InputPhoneNumber v-model="formFields.phone"/>
            </el-form-item>
            <el-form-item label="Email" prop="email">
                <el-input v-model.trim="formFields.email"></el-input>
            </el-form-item>
            <el-form-item label="Role" prop="role" required>
                <el-select v-model="formFields.role" placeholder="Role">
                    <el-option
                    v-for="item in options"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                    </el-option>
                </el-select>
                <el-tooltip class="item" effect="dark" placement="top-start">
                    <div slot="content">
                        <div class="mb-2">Admin: Access to all of Hera</div>
                        <div class="mb-2">Dispatcher: Excludes access to settings</div>
                        <div class="mb-2">Supervisor: Excludes access to settings and accident information </div>
                        <div>Team Lead: Excludes access to settings, accident, medical, and OSHA information</div>
                    </div>
                    <div class="text-gray-700 text-xs inline ml-4"><i class="uil uil-question-circle text-base"></i></div>
                </el-tooltip>
            </el-form-item>
            <el-form-item class="settingsSwitch" label="" prop="permissionLogin">
                <el-switch v-model="formFields.permissionLogin"></el-switch> Is Active?
            </el-form-item>
            <p class="py-3 text-lg">Notifications</p>
            <p class="pb-5 text-xs text-gray-600">Receieve notifications for...</p>

            <div class="flex justify-left">
                <p class="w-30"></p>
                <p class="ml-26 text-right">In App</p>
                <p class="ml-26 text-right">SMS</p>
                <p class="ml-24 text-right">Emails</p>
            </div>

            <div class="flex">
                <p class="pt-2 w-30">New Tasks</p>
                <el-form-item>
                    <el-checkbox disabled :value="true"></el-checkbox>
                </el-form-item>
                <el-form-item>
                    <el-checkbox v-model="formFields.receiveSmsTaskAssignments"></el-checkbox>
                </el-form-item>
                <el-form-item>
                    <el-checkbox v-model="formFields.receiveEmailTaskAssignments"></el-checkbox>
                </el-form-item>
            </div>

            <div class="flex">
                <p class="pt-2 w-30">Task Reminders</p>
                <el-form-item>
                    <el-checkbox disabled :value="true"></el-checkbox>
                </el-form-item>
                <el-form-item>
                    <el-checkbox v-model="formFields.receiveSmsTaskReminders"></el-checkbox>
                </el-form-item>
                <el-form-item>
                    <el-checkbox v-model="formFields.receiveEmailTaskReminders"></el-checkbox>
                </el-form-item>
            </div>
        </el-form>

        <el-dialog
          id="show-popup-update-email"
          title="Warning"
          :visible.sync="showPopupUpdateEmail"
          width="30%"
          :show-close="false"
          :close-on-click-modal="false"
          >
          <span >You are about to update the email address for this user. <span v-if="allowResendEmail">Would you like to resend an email with their temporary password to the new email address?</span></span>
          <span slot="footer" class="dialog-footer">
            <ButtonGroupWrapper>
                <template slot-scope="{disabled, setDisabled}">
                    <el-button
                        size="small"
                        :disabled="disabled"
                        @click="showPopupUpdateEmail=false"
                    >
                        Cancel
                    </el-button>
                    <ButtonWrapper
                        v-if="allowResendEmail"
                        type="primary"
                        size="small"
                        :clickHandler="updateUser"
                        :handlerArgs="[false, true, true]"
                        :disabled="disabled"
                        @cb-start="setDisabled(true)"
                        @cb-end="setDisabled(false)"
                    >
                        Update & Resend
                    </ButtonWrapper>
                    <ButtonWrapper
                        type="primary"
                        size="small"
                        :clickHandler="updateUser"
                        :handlerArgs="[false, true, false]"
                        :disabled="disabled"
                        @cb-start="setDisabled(true)"
                        @cb-end="setDisabled(false)"
                    >
                        Update
                    </ButtonWrapper>
                </template>
            </ButtonGroupWrapper>
          </span>
        </el-dialog>
    </div>
</template>

<script>
import { getTenant, listUsers, staffsByGroupForLinking } from '../query'
import Validator from '@/utilities/FormValidator'
import { cleanPhoneNumber, phoneNumberFormatUS } from '@/utilities/phoneNumberFormat'
import { createUser, deleteUser, updateTenant } from '@/graphql/mutations'
import { updateUser } from '@/store/mutations'
import { updateZohoAccountTenant, updateZohoAccountUser } from '../../../../components/Zoho/zoho'
import { getUser } from '../query'
import { mapMutations } from 'vuex'
import { Auth, API } from 'aws-amplify';
import { createNotification } from '@/graphql/mutations'
import { clearVisibleStaffCache, isHierarchicalPermissionsEnabled } from '@/utilities/visibleStaffSet'
import { getTenantRoles, getUserRoles as fetchUserRoles, setUserRoles, getEffectiveLocksForUser } from '@/utilities/athenaHeraApi'
import { getActiveReportsToValue, getAvailableManagerUsers, getDownlineIds, validateReportsToSelection } from '../hierarchyUtils'
import InputPhoneNumber from '@/components/InputPhoneNumber.vue'
import ButtonWrapper from '@/components/shared/ButtonWrapper/ButtonWrapper';
import ButtonGroupWrapper from '@/components/shared/ButtonGroupWrapper/ButtonGroupWrapper';
import { getTenantFeaturePermissions } from '@/middlewares/permissions';
import { extractTenantGroup } from '@/utilities/authHelpers'
import { resolveSchedulingRosteringPermission as schedulingRosteringRule } from '@/views/Settings/Users/schedulingRosteringPermission'
import { 
    HERA_MESSENGER_NEW_MESSAGE,
    DAILY_ROSTER_IMPORT_COMPLETE,
    DAILY_PERFORMANCE_DATA_IMPORT_COMPLETE,
    WEEKLY_PERFORMANCE_DATA_IMPORT_COMPLETE,
    TASK_REMINDER,
    NEW_TASK,
    EXPORT_READY_FOR_DOWNLOAD,
    DOCUMENT_SIGNATURE_ACKNOWLEDGMENT,
    VEHICLE_MAINTENANCE_REMINDER,
    COUNSELING_SIGNED_BY_ASSOCIATE,
    ASSOCIATE_MATCHING_SCREEN_LANDING,
    PAYROLL_PROVIDER_MATCHES_FOUND
} from '@/utilities/constants/notificationTypes'


export default {
  
    name: 'UserForm',
    props:['formFields' ],
    components: {
        InputPhoneNumber,
        ButtonWrapper,
        ButtonGroupWrapper,
    },
    data(){
        return{
            keyPhoneNumber:'',
            loading: false,
            allActiveUsers: [],
            allStaff: [],
            tenantRoles: [],
            assignedRoleIds: [],
            effectiveLocks: [],
            overriddenKeys: [],
            scopeOptions: [
                { label: 'All Records', value: 'all' },
                { label: 'Subordinates & Self', value: 'subordinatesAndSelf' },
                { label: 'Subordinates Only', value: 'subordinatesOnly' }
            ],
            group: '',
            tenantId: '',
            formFieldsRoute: {},
            editUserDataPrev: {},
            editPrevEmail: '',
            phone: null,
            results: null,
            isEmailSuppressed: null,
            USER_PERMISSIONS: process.env.VUE_APP_USER_PERMISSIONS === "1",
            options: [
                {
                    value: 'admin',
                    label: 'Admin'
                },
                {
                    value: 'dispatcher',
                    label: 'Dispatcher'
                },
                {
                    value: 'supervisor',
                    label: 'Supervisor'
                },
                {
                    value: 'teamlead',
                    label: 'Team Lead'
                }
            ],
            notificationOptions: [
                { title: 'New Task', description: 'Receive notifications when new tasks are created for you.', smsField: 'receiveSmsTaskAssignments', emailField: 'receiveEmailTaskAssignments'},
                { title: 'Task Reminders', description: 'Receive notifications when upcoming tasks are due.', smsField: 'receiveSmsTaskReminders', emailField: 'receiveEmailTaskReminders'}
            ],
            accountOptions: [
                { key: 'heraLogin', title: 'Can log in to Hera', description: 'Can access all other Hera features including, Daily Rostering, Performance & Coaching, '+this.$t('label.associate')+' Management, Vehicle Management, ' + this.getNavTitle('FnR') + ', etc.', field: 'permissionLogin'},
                { key: 'fullAccess', title: 'Has Full Admin Access', description: 'In addition to all the permissions below, users can access and edit all Hera settings, including payment info, invoices, company details, users, user permissions, '+this.$t('label.associate')+' Performance Report settings, and message logs. To disable individual permissions, Full Admin Access must be disabled.',  field: 'permissionFullAccess'}
            ],
            permissionOptions: [
                { 
                    key: 'aiChat', 
                    title: 'Module: Hera AI Chat', 
                    description: "The user can access the Hera AI Chat feature. This permission is only effective when the tenant has Hera AI Chat access granted, enabled, and tenant-level permission turned on.", 
                    field: 'permissionAIChat' 
                },
                {   key: 'labels', 
                    title: "Company Settings: Manage Labels", 
                    description: `The user can create, edit, and delete Labels, which can be applied to ${this.$t('label.associates')} and Vehicles. (Note: All users with access to "${this.$t('label.associate')} Management" and "Vehicle Management" will be able to apply and remove Labels to and from ${this.$t('label.associates')} and Vehicles; but they must have this permission to create and edit Label names and details.)`,  
                    field: 'permissionManageLabels'
                },
                {
                    key: 'documents',
                    title: `Associate Management: ${this.$t('label.associate')} Documents`,
                    description: `The user can view, create, edit, and delete ${this.$t('label.associates_')} documents.`,
                    field: 'permissionDocuments',
                    scopeField: 'permissionDocumentsScope'
                },
                {
                    key: 'manageCounselings',
                    title: 'Performance & Coaching: Counselings',
                    description: "The user can access, create, and edit Counselings and related reports in Hera. Without this permission, the user will see a message under the 'Counseings' sub-tab of the 'Performance & Coaching' tab that instructs them to contact their administrator for access.",
                    field: 'permissionManageCounselings',
                    scopeField: 'permissionManageCounselingsScope'
                },
                {
                    key: 'deleteCounselings',
                    title: 'Performance & Coaching: Delete Counselings ',
                    description: "The user can delete any Counseling, regardless of its status.",
                    field: 'permissionDeleteCounselings',
                },
                {
                    key: 'accidentReports',
                    title: 'Vehicle & Associate Management: Incident Records',
                    description: 'The user can view, create, edit, and delete Accident Records.',
                    field: 'permissionAccidents',
                    scopeField: 'permissionAccidentsScope'
                },
                {
                    key: 'injuryReports',
                    title: 'Associate Management: Injury Records',
                    description: 'The user can view, create, edit, and delete Injury Records.',
                    field: 'permissionInjuries',
                    scopeField: 'permissionInjuriesScope'
                },
                {
                    key: 'drugTests',
                    title: 'Associate Management: Drug Tests',
                    description: `The user can view, create, edit, and delete ${this.$t('label.associates_')} drug tests.`,
                    field: 'permissionDrugTests',
                    scopeField: 'permissionDrugTestsScope'
                },
                { 
                    key: 'dailyRostering', 
                    title: 'Module: Daily Rostering', 
                    description: "The user can access the Daily Rostering module in Hera. Without this permission, the user will see a message under the 'Daily Rostering' tab that instructs them to contact their administrator for access.", 
                    field: 'permissionDailyRostering' 
                },
                {
                    key: 'heraMessenger',
                    title: 'Module: Hera Messenger',
                    description: `The user can access the Hera Messenger module in Hera. They can receive notifications in Hera, read incoming messages, and send individual messages to Associates. Without this permission, the user will see a message under the 'Hera Messenger' tab that instructs them to contact their administrator for access. (Note: Even without this permission, the user will be able to send broadcast messages if they have the 'Module: Daily Rostering' permission.)`,
                    field: 'permissionMessenger',
                    scopeField: 'permissionMessengerScope'
                },
                {
                    key: 'performanceCoaching',
                    title: 'Module: Performance & Coaching',
                    description: "The user can access the Performance & Coaching module in Hera. Without this permission, the user will see a message under the 'Performance & Coaching' tab that instructs them to contact their administrator for access, and they will not have access to Performance & Coaching reports in the Tasks & Reports module.",
                    field: 'permissionPerformanceCoaching',
                    scopeField: 'permissionPerformanceCoachingScope'
                },
                {
                    key: 'customLists',
                    title: 'Company Settings: Manage Custom Lists',
                    description: "The user can add, edit, and delete options for all the Custom Lists available in Hera by going to Company Settings. This permission automatically includes all permissions that allow users to add options to Custom Lists.",
                    field: 'permissionCustomLists'
                },
                { 
                    key: 'DAManagement', 
                    title: `Module: ${this.$t('label.associate')} Management`, 
                    description: `The user can access the Associate Management module in Hera. Without this permission, the user will see a message under the 'Associate Management' tab that instructs them to contact their administrator for access, and they will not have access to Associate Management reports in the ${this.getNavTitle('FnR')} module.`, 
                    field: 'permissionDAManagement' 
                },
                { 
                    key: 'vehicleManagement', 
                    title: 'Module: Vehicle Management', 
                    description: "The user can access the Vehicle Management module in Hera. Without this permission, the user will see a message under the 'Vehicle Management' tab that instructs them to contact their administrator for access.", 
                    field: 'permissionVehicleManagement' 
                },
                {
                    key: 'vehicleImages',
                    title: 'Vehicle Management: Vehicle Images',
                    description: "The user can view, upload, and delete Vehicle images that are stored in each Vehicle's record.",
                    field: 'permissionVehicleImages'
                },
                {
                    key: 'vehicleRelatedDocuments',
                    title: 'Vehicle Management: Related Documents',
                    description: "The user can view, upload, and delete Vehicle related documents that are stored in each Vehicle's record.",
                    field: 'permissionVehicleRelatedDocuments'
                },
                { 
                    key: 'tasksReports', 
                    title: 'Module: ' + this.getNavTitle('FnR'), 
                    description: "The user can access the " + this.getNavTitle('FnR') + " module in Hera. Without this permission, the user will see a message under the '" + this.getNavTitle('FnR') + "' tab that instructs them to contact their administrator for access.", 
                    field: 'permissionTasksReports' 
                },
                { 
                    key: 'messageTemplate', 
                    title: 'Company Settings: Manage Message Templates', 
                    description: "The user can create, edit, and delete Message Templates, which can be used in Hera Messenger.", 
                    field: 'permissionMessageTemplate' 
                },
                {
                    key: 'rosterChecklists',
                    title: 'Company Settings: Manage Roster Checklists Templates',
                    description: "The user can create, edit, and delete Roster Checklist Templates, which can be assigned to Associates and Hera Users. (Note: All users with access to \"Daily Rostering\" or who are assigned to a Roster Checklist will be able to view and complete Roster Checklists.)",
                    field: 'permissionCompanySettingsRosterChecklistTemplates'
                },
                {
                    key: 'schedulingRostering',
                    title: 'Company Settings: Manage Scheduling & Rostering Settings',
                    description: "The user can access and manage Scheduling & Rostering configuration under Company Settings.",
                    field: 'permissionCompanySettingsSchedulingRostering'
                },
                { 
                    key: 'vehiclePhotoLogs', 
                    title: 'Company Settings: Manage Vehicle Photo Log Settings', 
                    description: "The user can edit company-wide settings for Vehicle Photo Logs by going to Company Settings.", 
                    field: 'permissionVehiclePhotoLogs' 
                },
                {
                    key:'companyDevices',
                    title: 'Module: Company Devices',
                    description: "The user can view and manage Company Devices. Note: users will still be able to select and view selected devices in other areas of Hera if they have access, such as Daily Roster or the Vehicle record.",
                    field: 'permissionCompanyDevices'
                },
                {
                    key:'locationDataFromCompanyDevices',
                    title: 'Company Devices: Location Data',
                    description: "The user can view location data across Hera (for example, on maps) made possible by Company Devices that are synced with Wireless Support.",
                    field: 'permissionLocationDataFromCompanyDevices'
                },
                { 
                    key: 'amazonChromePlugin', 
                    title: 'Module: Hera Amazon Chrome Plugin', 
                    description: "The user can access the Hera Amazon Chrome Plugin feature. This permission is only effective when the tenant has Hera Amazon Chrome Plugin access granted, enabled, and tenant-level permission turned on.", 
                    field: 'permissionAmazonChromePlugin' 
                },
                {
                    key: 'phoneAutomation',
                    title: 'Module: Phone Automation',
                    description: "The user can access the Phone Automation feature. This permission is only effective when the tenant has Phone Automation access granted, enabled, and tenant-level permission turned on.",
                    field: 'permissionPhoneAutomation'
                },
                { 
                    key: 'payrollProvider', 
                    title: 'Module: Payroll Provider', 
                    description: 'The user can access the Payroll Provider feature.', 
                    field: 'permissionPayrollProvider' 
                },
                { 
                    key: 'inventoryManagement', 
                    title: 'Module: Inventory Management', 
                    description: "The user can access the Inventory Management module in Hera. Without this permission, the user will see a message under the 'Inventory Management' tab that instructs them to contact their administrator for access.", 
                    field: 'permissionInventoryManagement' 
                }
            ],
            rules: {
                firstName: [
                    { validator: (rule, value, cb) => (value && value.trim()) ? cb() : cb(new Error('First Name is required')), trigger: 'blur' },
                ],
                lastName: [
                    { validator: (rule, value, cb) => (value && value.trim()) ? cb() : cb(new Error('Last Name is required')), trigger: 'blur' },
                ],
                email: [
                    { message: 'Please enter a valid email', trigger: 'blur', validator: Validator.email },
                ],
                role: [
                    { validator: (rule, value, cb) => (value && value.trim()) ? cb() : cb(new Error('Role is required')), trigger: 'blur' },
                ],
                phone: [
                    { validator: (rule, value, cb) => (value && value.trim()) ? cb() : cb(new Error('Phone is required')), trigger: 'blur' },
                    { validator: Validator.validatePhoneNumberUser,  trigger: 'blur', params: { daId: this.$route.query.id, group: this.$route.query.group } }
                ]
            },
            showPopupUpdateEmail: false,
            allowResendEmail: false
        }
    },

    async mounted(){
        this.loading = true
        const featurePermissions = getTenantFeaturePermissions();
        if (featurePermissions?.featureEnabledFilesManagement && featurePermissions?.featureAccessFilesManagement) {
            this.permissionOptions.push({
                key: 'otherFiles',
                title: 'Files & Reports: Other Files',
                description: "The user can access and manage other files under the 'Files' sub-tab of the Files & Reports module.",
                field: 'permissionOtherFiles'
            });
        }
        this.permissionOptions.sort((a, b) => a.title.localeCompare(b.title));
        if(this.USER_PERMISSIONS){
            try{
                if(this.$route.query.id){
                    let user = await this.api(getUser, { id: this.$route.query.id })
                    user = user.data.getUser
                    this.isEmailSuppressed = user.emailAddedToSuppressionListDateTime;
                    //set default value to true is persmisson is null
                    user.permissionDailyRostering = user.permissionDailyRostering ?? true
                    user.permissionMessenger = user.permissionMessenger ?? true
                    user.permissionPerformanceCoaching = user.permissionPerformanceCoaching ?? true
                    user.permissionDAManagement = user.permissionDAManagement ?? true
                    user.permissionManageLabels = user.permissionManageLabels ?? true
                    user.permissionCompanySettingsRosterChecklistTemplates = user.permissionCompanySettingsRosterChecklistTemplates ?? true
                    user.permissionCompanySettingsSchedulingRostering = user.permissionCompanySettingsSchedulingRostering ?? false
                    user.permissionMessageTemplate = user.permissionMessageTemplate ?? true
                    user.permissionVehicleManagement = user.permissionVehicleManagement ?? true
                    user.permissionTasksReports = user.permissionTasksReports ?? true
                    user.permissionVehiclePhotoLogs = user.permissionVehiclePhotoLogs ?? true
                    user.permissionDeleteCounselings = user.permissionDeleteCounselings ?? true
                    user.permissionVehicleImages = user.permissionVehicleImages ?? true
                    user.permissionVehicleRelatedDocuments = user.permissionVehicleRelatedDocuments ?? true
                    user.permissionOtherFiles = user.permissionOtherFiles ?? true
                    user.permissionAmazonChromePlugin = user.permissionAmazonChromePlugin ?? true
                    user.permissionAIChat = user.permissionAIChat ?? true
                    user.permissionCompanyDevices = user.permissionCompanyDevices ?? true
                    user.permissionLocationDataFromCompanyDevices = user.permissionLocationDataFromCompanyDevices ?? true
                    user.permissionPhoneAutomation = user.permissionPhoneAutomation ?? true
                    user.permissionPayrollProvider = user.permissionPayrollProvider ?? true
                    user.permissionInventoryManagement = user.permissionInventoryManagement ?? true


                    // AWSJSON comes back parsed — keep it as a JSON string for save
                    if (user.rolePermissionOverrides && typeof user.rolePermissionOverrides !== 'string') {
                        user.rolePermissionOverrides = JSON.stringify(user.rolePermissionOverrides)
                    }
                    user.permissionDocumentsScope = user.permissionDocumentsScope ?? 'all'
                    user.permissionManageCounselingsScope = user.permissionManageCounselingsScope ?? 'all'
                    user.permissionAccidentsScope = user.permissionAccidentsScope ?? 'all'
                    user.permissionInjuriesScope = user.permissionInjuriesScope ?? 'all'
                    user.permissionDrugTestsScope = user.permissionDrugTestsScope ?? 'all'
                    user.permissionPerformanceCoachingScope = user.permissionPerformanceCoachingScope ?? 'all'
                    user.permissionMessengerScope = user.permissionMessengerScope ?? 'all'

                    this.formFieldsRoute = {...user}
                    this.formFieldsRoute.phone = phoneNumberFormatUS(this.formFieldsRoute.phone)
                    this.editUserDataPrev = {...user}
                    this.editPrevEmail = user.email
                    this.editPrevRole = user.role

                    // Temporarily clear UUID fields so they don't flash in the UI
                    // They will be restored after dropdown options are loaded
                    this._pendingReportsToUserId = this.formFieldsRoute.reportsToUserId
                    this._pendingSamePersonAsStaffId = this.formFieldsRoute.samePersonAsStaffId
                    this.formFieldsRoute.reportsToUserId = null
                    this.formFieldsRoute.samePersonAsStaffId = null
                }
                else{
                    this.formFieldsRoute = {
                        permissionLogin: true,
                        permissionFullAccess: false,
                        permissionDocuments: true,
                        permissionManageCounselings: true,
                        permissionDeleteCounselings: false,
                        permissionAccidents: true,
                        permissionInjuries: true,
                        permissionDrugTests: true,
                        permissionDailyRostering: true,
                        permissionMessenger: true,
                        permissionPerformanceCoaching: true,
                        permissionDAManagement: true,
                        receiveSmsTaskReminders: true,
                        receiveEmailTaskReminders: true,
                        receiveSmsTaskAssignments: true,
                        receiveEmailTaskAssignments: true,
                        permissionDailyRostering: true,
                        permissionCustomLists: true,
                        permissionPerformanceCoaching: true,
                        permissionVehicleManagement: true,
                        permissionTasksReports: true,
                        permissionMessageTemplate: true,
                        permissionVehiclePhotoLogs: true,
                        permissionManageLabels: true,
                        permissionCompanySettingsRosterChecklistTemplates: true,
                        permissionCompanySettingsSchedulingRostering: false,
                        permissionVehicleImages: true,
                        permissionVehicleRelatedDocuments: true,
                        permissionOtherFiles: true,
                        permissionAIChat: true,
                        permissionCompanyDevices: true,
                        permissionLocationDataFromCompanyDevices: true,
                        permissionAmazonChromePlugin: true,
                        permissionPhoneAutomation: true,
                        reportsToUserId: null,
                        samePersonAsStaffId: null,
                        permissionDocumentsScope: 'all',
                        permissionManageCounselingsScope: 'all',
                        permissionAccidentsScope: 'all',
                        permissionInjuriesScope: 'all',
                        permissionDrugTestsScope: 'all',
                        permissionPerformanceCoachingScope: 'all',
                        permissionMessengerScope: 'all'
                    }

                    // Default new users to report to the tenant owner
                    const tenantId = this.$route.query.tenantId
                    if (tenantId) {
                        const tenantResult = await this.api(getTenant, { id: tenantId })
                        const ownerUserId = tenantResult?.data?.getTenant?.ownerUserId
                        if (ownerUserId) {
                            this.formFieldsRoute.reportsToUserId = ownerUserId
                        }
                    }
                }
                this.group = this.$route.query.group || this.$store.state.userInfo?.tenant?.group
                this.tenantId = this.$route.query.tenantId
                this.companyName = this.$route.query.companyName

                // Load users, staff, roles, and locks for dropdowns
                // Await so the form stays in loading state until options are ready
                // (prevents showing raw UUIDs before names resolve)
                await Promise.all([
                    this.loadUsersAndStaff(),
                    this.loadRolesAndLocks()
                ])

                // Restore the UUID fields now that dropdown options are loaded
                // Only restore if the value can be resolved to a name in the options
                if (this._pendingReportsToUserId !== undefined) {
                    const canResolve = this.allActiveUsers.find(u => u.id === this._pendingReportsToUserId)
                    this.formFieldsRoute.reportsToUserId = canResolve ? this._pendingReportsToUserId : null
                    this._pendingReportsToUserId = undefined
                }
                if (this._pendingSamePersonAsStaffId !== undefined) {
                    const canResolve = this.allStaff.find(s => s.id === this._pendingSamePersonAsStaffId)
                    this.formFieldsRoute.samePersonAsStaffId = canResolve ? this._pendingSamePersonAsStaffId : null
                    this._pendingSamePersonAsStaffId = undefined
                }
            }
            catch(e){
                console.error(e)
                this.displayUserError(e)
            }
            finally{
                this.loading = false
            }
        }
    },
    computed: {
        isEmailDisabled() {
            const isEmailUpdated = this.editPrevEmail != this.formFieldsRoute.email;
            const canEnableEmail = this.$store.state.userInfo.email == this.editPrevEmail;
            return (!isEmailUpdated && !canEnableEmail && !!this.isEmailSuppressed)
        },
        availableManagers() {
            const editingId = this.formFieldsRoute.id
            const managers = getAvailableManagerUsers(this.allActiveUsers, editingId)
            // Include the currently-assigned manager even if inactive,
            // so the dropdown shows their name instead of a raw UUID
            const currentId = this.formFieldsRoute.reportsToUserId
            if (currentId && !managers.find(u => u.id === currentId)) {
                const current = this.allActiveUsers.find(u => u.id === currentId)
                if (current) managers.unshift(current)
            }
            return managers
        },
        availableStaff() {
            const staff = this.allStaff.filter(s => s.status !== 'Inactive')
            // Include the currently-linked associate even if inactive,
            // so the dropdown shows their name instead of a raw UUID
            const currentId = this.formFieldsRoute.samePersonAsStaffId
            if (currentId && !staff.find(s => s.id === currentId)) {
                const current = this.allStaff.find(s => s.id === currentId)
                if (current) staff.unshift(current)
            }
            return staff
        },
        directReports() {
            if (!this.formFieldsRoute.id) return []
            if (!this.formFieldsRoute.permissionLogin) return []
            return this.allActiveUsers.filter(u => u.permissionLogin && u.reportsToUserId === this.formFieldsRoute.id)
        },
        locksByPermissionKey() {
            const map = {}
            for (const lock of this.effectiveLocks) {
                map[lock.permissionKey] = lock
            }
            return map
        },
        overrideCount() {
            return this.effectiveLocks.filter(l => l.isOverridden).length
        },
        reportsToValue: {
            get() {
                if (!this.allActiveUsers.length) return null
                return getActiveReportsToValue(this.allActiveUsers, this.formFieldsRoute.reportsToUserId)
            },
            set(val) {
                this.formFieldsRoute.reportsToUserId = val
            }
        },
        linkedAssociateValue: {
            get() {
                if (!this.allStaff.length) return null
                return this.formFieldsRoute.samePersonAsStaffId
            },
            set(val) {
                this.formFieldsRoute.samePersonAsStaffId = val
            }
        }
    },
    methods:{
        ...mapMutations([
            'setSessionInfo', 'setUserInfo'
        ]),

        getDownlineIds(userId) {
            return getDownlineIds(this.allActiveUsers, userId)
        },

        validateReportingChain() {
            const result = validateReportsToSelection(
                this.allActiveUsers,
                this.formFieldsRoute.id,
                this.formFieldsRoute.reportsToUserId
            )
            if (!result.valid) {
                this.displayUserError(result.message)
                return false
            }
            return true
        },

        async loadRolesAndLocks() {
            try {
                // Skip if Athena API is not configured (avoids error toast in local dev)
                if (!process.env.VUE_APP_ATHENA_HERA_API_URL) return

                // Skip role/locks for cross-tenant edits — Athena API is tenant-scoped
                const loggedInGroup = this.$store.state.userInfo?.tenant?.group
                const editingGroup = this.$route.query.group
                if (editingGroup && loggedInGroup && editingGroup !== loggedInGroup) return

                // Always load tenant roles (needed for both new and edit)
                const roles = await getTenantRoles()
                this.tenantRoles = roles || []

                // Only fetch user-specific data for existing users
                if (!this.formFieldsRoute.id) return

                const [userRoles, locksData] = await Promise.all([
                    fetchUserRoles(this.formFieldsRoute.id),
                    getEffectiveLocksForUser(this.formFieldsRoute.id)
                ])
                this.assignedRoleIds = (userRoles || []).map(r => r.roleId)
                this.effectiveLocks = locksData?.locks || []
                this.overriddenKeys = locksData?.overrides || []

                // Apply locked values to the form for non-overridden locks
                for (const lock of this.effectiveLocks) {
                    if (!lock.isOverridden) {
                        this.formFieldsRoute[lock.permissionKey] = lock.lockedValue
                        if (lock.lockedScope != null) {
                            const scopeField = lock.permissionKey + 'Scope'
                            if (this.formFieldsRoute[scopeField] !== undefined) {
                                this.formFieldsRoute[scopeField] = lock.lockedScope
                            }
                        }
                    }
                }
            } catch (e) {
                console.error('Error loading roles/locks:', e)
                this.displayUserError('Failed to load role permissions. Some permission locks may not be enforced.')
            }
        },

        async handleRoleChange(newRoleIds) {
            try {
                await setUserRoles(this.formFieldsRoute.id, newRoleIds)
                // Reload effective locks since roles changed
                const locksData = await getEffectiveLocksForUser(this.formFieldsRoute.id)
                this.effectiveLocks = locksData?.locks || []
                this.overriddenKeys = locksData?.overrides || []

                // Apply new lock values to the form
                for (const lock of this.effectiveLocks) {
                    if (!lock.isOverridden) {
                        this.formFieldsRoute[lock.permissionKey] = lock.lockedValue
                        if (lock.lockedScope != null) {
                            const scopeField = lock.permissionKey + 'Scope'
                            if (this.formFieldsRoute[scopeField] !== undefined) {
                                this.formFieldsRoute[scopeField] = lock.lockedScope
                            }
                        }
                    }
                }
            } catch (e) {
                console.error('Error updating user roles:', e)
                this.displayUserError('Failed to update user roles')
            }
        },

        isPermissionLocked(field) {
            const lock = this.locksByPermissionKey[field]
            return lock && !lock.isOverridden
        },

        getLockedByLabel(field) {
            const lock = this.locksByPermissionKey[field]
            if (!lock) return null
            return `Locked by ${lock.lockedByRoles.join(', ')}`
        },

        async toggleOverride(field) {
            const lock = this.locksByPermissionKey[field]
            if (!lock) return
            const overrides = [...this.overriddenKeys]
            const idx = overrides.indexOf(field)
            if (idx >= 0) {
                overrides.splice(idx, 1)
                // Revert to locked value
                this.formFieldsRoute[field] = lock.lockedValue
                if (lock.lockedScope != null) {
                    const scopeField = field + 'Scope'
                    if (this.formFieldsRoute[scopeField] !== undefined) {
                        this.formFieldsRoute[scopeField] = lock.lockedScope
                    }
                }
            } else {
                overrides.push(field)
            }
            this.overriddenKeys = overrides
            this.formFieldsRoute.rolePermissionOverrides = overrides.length > 0 ? JSON.stringify(overrides) : null
            // Update the lock's isOverridden state locally
            lock.isOverridden = idx < 0
        },

        async loadUsersAndStaff() {
            try {
                const group = this.$route.query.group
                const [usersItems, staffItems] = await Promise.all([
                    this.gLoadListAll(listUsers, { filter: { group: { contains: group } }, limit: 1000 }, 'listUsers'),
                    this.gLoadListAll(staffsByGroupForLinking, { group: group, limit: 1000 }, 'staffsByGroup')
                ])
                this.allActiveUsers = usersItems
                this.allStaff = staffItems
            } catch (e) {
                console.error('Error loading users/staff for dropdowns', e)
            }
        },

        /**
         * Tenant feature flags for the Scheduling & Rostering module, or null when
         * they cannot be trusted for the user being edited.
         *
         * getTenantFeaturePermissions() reads the acting admin's tenant from the
         * store. On a cross-tenant edit (a system admin opening a user in another
         * tenant via the tenantId route param) those flags describe the wrong
         * tenant, so return null rather than risk granting the permission from an
         * unrelated tenant's module state. An explicit prior grant is then the only
         * thing that can keep it on. The null check also keeps updateToggles from
         * throwing if the store has no tenant yet.
         */
        schedulingRosteringTenantFlags(){
            const actingTenant = this.$store.state.userInfo?.tenant
            if(!actingTenant) return null
            if(this.tenantId && this.tenantId !== actingTenant.id) return null
            return getTenantFeaturePermissions()
        },

        /**
         * Resolve the "Company Settings: Manage Scheduling & Rostering Settings"
         * permission for the Full Admin revoke path.
         *
         * Scheduling & Rostering is gated by a tenant feature flag. When the module
         * is enabled for the tenant the permission follows every other permission and
         * stays granted. When the module is disabled it must go back off unless an
         * administrator had explicitly granted it before this form session began, so
         * that granting and then revoking Full Admin does not silently enable
         * configuration for an unreleased feature.
         */
        resolveSchedulingRosteringPermission(){
            // editUserDataPrev is the load-time snapshot, taken after the
            // null-normalization pass, and is never reassigned during the session.
            const priorStoredValue = this.editUserDataPrev
                ? this.editUserDataPrev.permissionCompanySettingsSchedulingRostering
                : undefined
            return schedulingRosteringRule(this.schedulingRosteringTenantFlags(), priorStoredValue)
        },

        async updateToggles(field){

            if (field === 'permissionManageCounselings') {
                if (!this.formFieldsRoute.permissionManageCounselings) {
                    this.formFieldsRoute.permissionDeleteCounselings = false;
                }
            }

            if(field === 'permissionFullAccess' && this.formFieldsRoute.permissionFullAccess == true){
                this.formFieldsRoute.permissionDocuments = true
                this.formFieldsRoute.permissionManageCounselings = true
                this.formFieldsRoute.permissionDeleteCounselings = true
                this.formFieldsRoute.permissionAccidents = true
                this.formFieldsRoute.permissionInjuries = true
                this.formFieldsRoute.permissionDrugTests = true
                this.formFieldsRoute.permissionDailyRostering = true
                this.formFieldsRoute.permissionMessenger = true
                this.formFieldsRoute.permissionPerformanceCoaching = true
                this.formFieldsRoute.permissionDAManagement = true
                this.formFieldsRoute.permissionCustomLists = true
                this.formFieldsRoute.permissionManageLabels = true
                this.formFieldsRoute.permissionMessageTemplate = true
                this.formFieldsRoute.permissionTasksReports = true
                this.formFieldsRoute.permissionVehicleManagement = true
                this.formFieldsRoute.permissionVehiclePhotoLogs = true
                this.formFieldsRoute.permissionCompanySettingsRosterChecklistTemplates = true
                this.formFieldsRoute.permissionCompanySettingsSchedulingRostering = true
                this.formFieldsRoute.permissionVehicleImages = true
                this.formFieldsRoute.permissionVehicleRelatedDocuments = true
                this.formFieldsRoute.permissionOtherFiles = true
                this.formFieldsRoute.permissionAmazonChromePlugin = true
                this.formFieldsRoute.permissionAIChat = true
                this.formFieldsRoute.permissionCompanyDevices = true
                this.formFieldsRoute.permissionLocationDataFromCompanyDevices = true
                this.formFieldsRoute.permissionAmazonChromePlugin = true
                this.formFieldsRoute.permissionPhoneAutomation = true
                this.formFieldsRoute.permissionDocumentsScope = 'all'
                this.formFieldsRoute.permissionManageCounselingsScope = 'all'
                this.formFieldsRoute.permissionAccidentsScope = 'all'
                this.formFieldsRoute.permissionInjuriesScope = 'all'
                this.formFieldsRoute.permissionDrugTestsScope = 'all'
                this.formFieldsRoute.permissionPerformanceCoachingScope = 'all'
                this.formFieldsRoute.permissionMessengerScope = 'all'
            }
            else if(field === 'permissionLogin' && this.formFieldsRoute.permissionLogin == false){
                this.formFieldsRoute.permissionFullAccess = false
                this.formFieldsRoute.permissionDocuments = false
                this.formFieldsRoute.permissionManageCounselings = false
                this.formFieldsRoute.permissionDeleteCounselings = false
                this.formFieldsRoute.permissionAccidents = false
                this.formFieldsRoute.permissionInjuries = false
                this.formFieldsRoute.permissionDrugTests = false
                this.formFieldsRoute.permissionDailyRostering = false
                this.formFieldsRoute.permissionMessenger = false
                this.formFieldsRoute.permissionPerformanceCoaching = false
                this.formFieldsRoute.permissionCustomLists = false
                this.formFieldsRoute.permissionDAManagement = false
                this.formFieldsRoute.permissionManageLabels = false
                this.formFieldsRoute.permissionMessageTemplate = false
                this.formFieldsRoute.permissionTasksReports = false
                this.formFieldsRoute.permissionVehicleManagement = false
                this.formFieldsRoute.permissionVehiclePhotoLogs = false
                this.formFieldsRoute.permissionCompanySettingsRosterChecklistTemplates = false
                this.formFieldsRoute.permissionCompanySettingsSchedulingRostering = false
                this.formFieldsRoute.permissionVehicleImages = false
                this.formFieldsRoute.permissionVehicleRelatedDocuments = false
                this.formFieldsRoute.permissionOtherFiles = false
                this.formFieldsRoute.permissionAmazonChromePlugin = false
                this.formFieldsRoute.permissionAIChat = false
                this.formFieldsRoute.permissionCompanyDevices = false
                this.formFieldsRoute.permissionLocationDataFromCompanyDevices = false
                this.formFieldsRoute.permissionPayrollProvider = false
                this.formFieldsRoute.permissionInventoryManagement = false
            }
            else if(field === 'permissionFullAccess' && this.formFieldsRoute.permissionFullAccess == false){
                // Revoking Full Admin leaves every other permission granted, which
                // matches the existing behaviour. Scheduling & Rostering is the one
                // exception: it is gated by an unreleased feature flag, so it must not
                // be left switched on by the grant above.
                this.formFieldsRoute.permissionCompanySettingsSchedulingRostering = this.resolveSchedulingRosteringPermission()
            }
            if((field === 'permissionFullAccess' && this.formFieldsRoute.permissionFullAccess == false) || 
               (field === 'permissionLogin' && this.formFieldsRoute.permissionLogin == false)){
                // check for at least one other full access user
                let tenantId = this.tenantId || this.$store.state.userInfo.tenant.id;
                const tenant = await this.api(getTenant, { id: tenantId })
                let users = tenant.data.getTenant.users.items

                let fullAccessUsers = users.filter(user =>{
                    return user.permissionFullAccess
                })
                let loginUsers = users.filter(user =>{
                    return user.permissionLogin
                })

                let numFullAccessUsers = fullAccessUsers.length
                let numLoginUsers = loginUsers.length

                if(numLoginUsers == 1 && loginUsers[0].id == this.formFieldsRoute.id && field === 'permissionLogin'){
                    this.formFieldsRoute.permissionLogin = true
                    if(numFullAccessUsers == 1 && fullAccessUsers[0].id == this.formFieldsRoute.id){
                        this.formFieldsRoute.permissionFullAccess = true
                        this.formFieldsRoute.permissionDocuments = true
                        this.formFieldsRoute.permissionManageCounselings = true
                        this.formFieldsRoute.permissionDeleteCounselings = false
                        this.formFieldsRoute.permissionAccidents = true
                        this.formFieldsRoute.permissionInjuries = true
                        this.formFieldsRoute.permissionDrugTests = true
                        this.formFieldsRoute.permissionDailyRostering = true
                        this.formFieldsRoute.permissionMessenger = true
                        this.formFieldsRoute.permissionPerformanceCoaching = true
                        this.formFieldsRoute.permissionDAManagement = true
                        this.formFieldsRoute.permissionCustomLists = true
                        this.formFieldsRoute.permissionManageLabels = true
                        this.formFieldsRoute.permissionMessageTemplate = true
                        this.formFieldsRoute.permissionTasksReports = true
                        this.formFieldsRoute.permissionVehicleManagement = true  
                        this.formFieldsRoute.permissionVehiclePhotoLogs = true                      
                        this.formFieldsRoute.permissionCompanySettingsRosterChecklistTemplates = true
                        this.formFieldsRoute.permissionCompanySettingsSchedulingRostering = true
                        this.formFieldsRoute.permissionVehicleImages = true
                        this.formFieldsRoute.permissionVehicleRelatedDocuments = true
                        this.formFieldsRoute.permissionOtherFiles = true
                        this.formFieldsRoute.permissionAmazonChromePlugin = true
                        this.formFieldsRoute.permissionAIChat = true
                        this.formFieldsRoute.permissionCompanyDevices = true
                        this.formFieldsRoute.permissionLocationDataFromCompanyDevices = true
                        this.formFieldsRoute.permissionPayrollProvider = true
                        this.formFieldsRoute.permissionInventoryManagement = true
                    }
                    this.displayUserError('Your account must have at least one user with login privileges.')
                }
                else if(numFullAccessUsers == 1 && fullAccessUsers[0].id == this.formFieldsRoute.id){
                    this.formFieldsRoute.permissionFullAccess = true
                    this.formFieldsRoute.permissionDocuments = true
                    this.formFieldsRoute.permissionManageCounselings = true
                    this.formFieldsRoute.permissionDeleteCounselings = false
                    this.formFieldsRoute.permissionAccidents = true
                    this.formFieldsRoute.permissionInjuries = true
                    this.formFieldsRoute.permissionDrugTests = true
                    this.formFieldsRoute.permissionDailyRostering = true
                    this.formFieldsRoute.permissionMessenger = true
                    this.formFieldsRoute.permissionPerformanceCoaching = true
                    this.formFieldsRoute.permissionDAManagement = true
                    this.formFieldsRoute.permissionCustomLists = true
                    this.formFieldsRoute.permissionManageLabels = true
                    this.formFieldsRoute.permissionMessageTemplate = true
                    this.formFieldsRoute.permissionTasksReports = true
                    this.formFieldsRoute.permissionVehicleManagement = true 
                    this.formFieldsRoute.permissionVehiclePhotoLogs = true
                    this.formFieldsRoute.permissionCompanySettingsRosterChecklistTemplates = true
                    this.formFieldsRoute.permissionCompanySettingsSchedulingRostering = true
                    this.formFieldsRoute.permissionVehicleImages = true
                    this.formFieldsRoute.permissionVehicleRelatedDocuments = true
                    this.formFieldsRoute.permissionOtherFiles = true
                    this.formFieldsRoute.permissionAmazonChromePlugin = true
                    this.formFieldsRoute.permissionAIChat = true
                    this.formFieldsRoute.permissionCompanyDevices = true
                    this.formFieldsRoute.permissionLocationDataFromCompanyDevices = true
                    this.formFieldsRoute.permissionPayrollProvider = true
                    this.formFieldsRoute.permissionInventoryManagement = true
                    if(field === 'permissionLogin') this.formFieldsRoute.permissionLogin = true
                    this.displayUserError('Your account must have at least one user with Full Access privileges.')
                }
                
            }            
        },

        async confirmUpdateUser(){
            // warn about editing your own permissions 
            if(this.formFieldsRoute.id == this.$store.state.userInfo.id && (
                this.editUserDataPrev.permissionLogin != this.formFieldsRoute.permissionLogin ||
                this.editUserDataPrev.permissionFullAccess != this.formFieldsRoute.permissionFullAccess ||
                this.editUserDataPrev.permissionDocuments != this.formFieldsRoute.permissionDocuments ||
                this.editUserDataPrev.permissionManageCounselings != this.formFieldsRoute.permissionManageCounselings ||
                this.editUserDataPrev.permissionDeleteCounselings != this.formFieldsRoute.permissionDeleteCounselings ||
                this.editUserDataPrev.permissionAccidents != this.formFieldsRoute.permissionAccidents ||
                this.editUserDataPrev.permissionInjuries != this.formFieldsRoute.permissionInjuries ||
                this.editUserDataPrev.permissionDrugTests != this.formFieldsRoute.permissionDrugTests ||
                this.editUserDataPrev.permissionDailyRostering != this.formFieldsRoute.permissionDailyRostering ||
                this.editUserDataPrev.permissionMessenger != this.formFieldsRoute.permissionMessenger ||
                this.editUserDataPrev.permissionPerformanceCoaching != this.formFieldsRoute.permissionPerformanceCoaching ||
                this.editUserDataPrev.permissionTasksReports != this.formFieldsRoute.permissionTasksReports ||
                this.editUserDataPrev.permissionVehicleManagement != this.formFieldsRoute.permissionVehicleManagement ||
                this.editUserDataPrev.permissionCustomLists != this.formFieldsRoute.permissionCustomLists ||
                this.editUserDataPrev.permissionDAManagement != this.formFieldsRoute.permissionDAManagement ||
                this.editUserDataPrev.permissionManageLabels != this.formFieldsRoute.permissionManageLabels ||
                this.editUserDataPrev.permissionCompanySettingsRosterChecklistTemplates != this.formFieldsRoute.permissionCompanySettingsRosterChecklistTemplates ||
                this.editUserDataPrev.permissionVehicleImages != this.formFieldsRoute.permissionVehicleImages ||
                this.editUserDataPrev.permissionVehicleRelatedDocuments != this.formFieldsRoute.permissionVehicleRelatedDocuments ||
                this.editUserDataPrev.permissionOtherFiles != this.formFieldsRoute.permissionOtherFiles ||
                this.editUserDataPrev.permissionAmazonChromePlugin != this.formFieldsRoute.permissionAmazonChromePlugin ||
                this.editUserDataPrev.permissionAIChat != this.formFieldsRoute.permissionAIChat ||
                this.editUserDataPrev.permissionCompanyDevices != this.formFieldsRoute.permissionCompanyDevices ||
                this.editUserDataPrev.permissionLocationDataFromCompanyDevices != this.formFieldsRoute.permissionLocationDataFromCompanyDevices ||
                this.editUserDataPrev.permissionAmazonChromePlugin != this.formFieldsRoute.permissionAmazonChromePlugin ||
                this.editUserDataPrev.permissionDocumentsScope != this.formFieldsRoute.permissionDocumentsScope ||
                this.editUserDataPrev.permissionManageCounselingsScope != this.formFieldsRoute.permissionManageCounselingsScope ||
                this.editUserDataPrev.permissionAccidentsScope != this.formFieldsRoute.permissionAccidentsScope ||
                this.editUserDataPrev.permissionInjuriesScope != this.formFieldsRoute.permissionInjuriesScope ||
                this.editUserDataPrev.permissionDrugTestsScope != this.formFieldsRoute.permissionDrugTestsScope ||
                this.editUserDataPrev.permissionPerformanceCoachingScope != this.formFieldsRoute.permissionPerformanceCoachingScope ||
                this.editUserDataPrev.permissionMessengerScope != this.formFieldsRoute.permissionMessengerScope
            )){
                try {
                    await this.$confirm('You are about to update the account access or permissions for the current user. This will log you out to apply the changes. Continue?', 'Warning', {
                        confirmButtonText: 'Update',
                        cancelButtonText: 'Cancel',
                        type: 'warning'
                    });
                    await this.updateUser(true, false, false);
                }
                catch (error) {
                    console.error("canceled update");
                    return;
                }
            }
            else if(
                this.editUserDataPrev.permissionLogin != this.formFieldsRoute.permissionLogin ||
                this.editUserDataPrev.permissionFullAccess != this.formFieldsRoute.permissionFullAccess ||
                this.editUserDataPrev.permissionDocuments != this.formFieldsRoute.permissionDocuments ||
                this.editUserDataPrev.permissionManageCounselings != this.formFieldsRoute.permissionManageCounselings ||
                this.editUserDataPrev.permissionDeleteCounselings != this.formFieldsRoute.permissionDeleteCounselings ||
                this.editUserDataPrev.permissionAccidents != this.formFieldsRoute.permissionAccidents ||
                this.editUserDataPrev.permissionInjuries != this.formFieldsRoute.permissionInjuries ||
                this.editUserDataPrev.permissionDrugTests != this.formFieldsRoute.permissionDrugTests ||
                this.editUserDataPrev.permissionDailyRostering != this.formFieldsRoute.permissionDailyRostering ||
                this.editUserDataPrev.permissionMessenger != this.formFieldsRoute.permissionMessenger ||
                this.editUserDataPrev.permissionPerformanceCoaching != this.formFieldsRoute.permissionPerformanceCoaching ||
                this.editUserDataPrev.permissionTasksReports != this.formFieldsRoute.permissionTasksReports ||
                this.editUserDataPrev.permissionVehicleManagement != this.formFieldsRoute.permissionVehicleManagement ||
                this.editUserDataPrev.permissionCustomLists != this.formFieldsRoute.permissionCustomLists ||
                this.editUserDataPrev.permissionDAManagement != this.formFieldsRoute.permissionDAManagement ||
                this.editUserDataPrev.permissionManageLabels != this.formFieldsRoute.permissionManageLabels ||
                this.editUserDataPrev.permissionCompanySettingsRosterChecklistTemplates != this.formFieldsRoute.permissionCompanySettingsRosterChecklistTemplates ||
                this.editUserDataPrev.permissionVehicleImages != this.formFieldsRoute.permissionVehicleImages ||
                this.editUserDataPrev.permissionVehicleRelatedDocuments != this.formFieldsRoute.permissionVehicleRelatedDocuments ||
                this.editUserDataPrev.permissionOtherFiles != this.formFieldsRoute.permissionOtherFiles ||
                this.editUserDataPrev.permissionAmazonChromePlugin != this.formFieldsRoute.permissionAmazonChromePlugin ||
                this.editUserDataPrev.permissionAIChat != this.formFieldsRoute.permissionAIChat ||
                this.editUserDataPrev.permissionCompanyDevices != this.formFieldsRoute.permissionCompanyDevices ||
                this.editUserDataPrev.permissionLocationDataFromCompanyDevices != this.formFieldsRoute.permissionLocationDataFromCompanyDevices ||
                this.editUserDataPrev.permissionAmazonChromePlugin != this.formFieldsRoute.permissionAmazonChromePlugin ||
                this.editUserDataPrev.permissionDocumentsScope != this.formFieldsRoute.permissionDocumentsScope ||
                this.editUserDataPrev.permissionManageCounselingsScope != this.formFieldsRoute.permissionManageCounselingsScope ||
                this.editUserDataPrev.permissionAccidentsScope != this.formFieldsRoute.permissionAccidentsScope ||
                this.editUserDataPrev.permissionInjuriesScope != this.formFieldsRoute.permissionInjuriesScope ||
                this.editUserDataPrev.permissionDrugTestsScope != this.formFieldsRoute.permissionDrugTestsScope ||
                this.editUserDataPrev.permissionPerformanceCoachingScope != this.formFieldsRoute.permissionPerformanceCoachingScope ||
                this.editUserDataPrev.permissionMessengerScope != this.formFieldsRoute.permissionMessengerScope
            ){
                try {
                    await this.$confirm('You are about to update the account access or permissions for another user. If they are currently logged in, they will be logged out to apply the changes. Continue?', 'Warning', {
                        confirmButtonText: 'Update',
                        cancelButtonText: 'Cancel',
                        type: 'warning'
                    });
                    await this.updateUser(false, true, false);
                }
                catch (error) {
                    console.error("canceled update");
                    return;
                }
            }
            // ask to resend invitation email if chaning email and user has not yet signed in before || status FORCE_CHANGE_PASSWORD
            else if(this.editUserDataPrev.email != this.formFieldsRoute.email){
                //* get user to check status
                try {
                    let cognitoUser = await this.getCognitoUser(this.editUserDataPrev.email)
                    this.allowResendEmail = cognitoUser.UserStatus === "FORCE_CHANGE_PASSWORD"
                } catch(e) {
                    this.allowResendEmail = false
                    console.error('getCognitoUser error:', e)
                }
                //* open popup for change email user.
                this.showPopupUpdateEmail = true
            }
            else {
                await this.updateUser(false, false, false);
            }
        },

        /** Update a users information */
      async updateUser(logoutCurrent, logoutOther, sendEmail){
        this.loading = true
        if (!this.group) {
          this.loading = false
          this.displayUserError("Unable to determine tenant group. Please refresh the page and try again.")
          return
        }
        try {

          // validate form
          await this.$refs.form.validate();
          if (!this.validateReportingChain()) return
          var formattedEmail = this.cleanEmail(this.formFieldsRoute.email)

          // add user to cognito admin group — only on permissionFullAccess toggle
          if(this.formFieldsRoute.permissionFullAccess && !this.editUserDataPrev.permissionFullAccess){
            await this.addUserToCognitoGroup(this.editPrevEmail.toLowerCase(), 'admin')
          }
          // remove user from cognito admin group — only on permissionFullAccess toggle
          else if(!this.formFieldsRoute.permissionFullAccess && this.editUserDataPrev.permissionFullAccess){
            await this.removeUserFromCognitoGroup(this.editPrevEmail.toLowerCase(), 'admin')
          }

          // edit email in cognito 
          var attributes = [
            {
              Name: 'email',
              Value: formattedEmail
            },
            {
              Name: 'email_verified',
              Value: 'true'
            },
            {
                Name: 'custom:tenant_name',
                Value: this.companyName
            }
          ]

          if(this.cleanEmail(this.editPrevEmail) != formattedEmail){
                var cognitoUser = await this.getCognitoUser(formattedEmail)
                if(cognitoUser.statusCode && cognitoUser.statusCode != 200 && cognitoUser.code != 'UserNotFoundException' ){
                    var error = {
                        code: cognitoUser.code,
                        message: cognitoUser.message
                    }
                    throw(error)
                }
                var enabled = cognitoUser.Enabled
                if(enabled){
                    throw{
                        errors: [
                            { message: "User already exists with provided email"}
                        ]
                    }
                }
           }

          await this.updateUserAttributes(this.editPrevEmail.toLowerCase(), attributes)

          var input = {
            ...this.formFieldsRoute
          }
          // Preserve the user's original group (from DB fetch) to prevent the api() wrapper from injecting the login user's tenant group — see HERA-8423
          // if the email was suppressed and the user has enabled email notifications for their account then clear the suppression and remove the email from the SES Suppression List
          if(this.formFieldsRoute.emailAddedToSuppressionListDateTime) {
            if (this.formFieldsRoute.receiveEmailTaskAssignments || this.formFieldsRoute.receiveEmailTaskReminders) {
                const body = {
                    id: this.formFieldsRoute.id,
                    email: this.formFieldsRoute.email,
                    table: 'user',
                    source: 'manual_update'
                }
                await API.post('sesSuppression', '/update-email-suppression', { body });
            }
          }
          input.email = formattedEmail
          input.phone = cleanPhoneNumber(input.phone, 'US')
          if(input.phone ==='') input.phone = null
          const result = await this.api(updateUser, {input: input})
          this.editUserModalOpen = false

          // resend activation email
          if(sendEmail){ 
            // resend cognito user
            let lowerEmail = this.formFieldsRoute.email.toLowerCase()
            let userId = this.formFieldsRoute.id
            try{
                await this.createCognitoUser(lowerEmail, userId, true)
                this.displayUserNotification({
                    type: 'success',
                    message: 'Password Resent',
                    title: 'Success'
                });
                this.showPopupUpdateEmail = false
            }catch(e){
                if(e?.response?.data?.message.includes('Resend not possible')){
                    e.response = 'Resend not possible. User has already changed their temporary password.'
                }
                console.error(e.response)
                this.displayUserError(e.response)
            }
          }
          // update zoho contact  
          if(!this.$store.getters.isZohoCrmDisabled){
            try{
            const resp = await this.updateZohoContact(result.data.updateUser)
            }catch(e){
            this.loading = false
            this.displayUserNotification({
                title: "Error",
                type: "error",
                message: e
            })
            }
          }
          // Clear visible staff cache since scope or reporting chain may have changed
          clearVisibleStaffCache()

          this.displayUserNotification({
            type: 'success',
            title: 'Success',
            message: 'User Updated'
          });
          this.loading = false
          this.showPopupUpdateEmail = false
          if(logoutCurrent){
            await Auth.signOut()
            this.setSessionInfo(null)
            this.setUserInfo({tenant: {}})
            this.$router.replace({name: 'Login'})
            return
          }
          else if(logoutOther){
            // logout updated user using notifcation system
            let input = {
                title: "User Permissions Updated",
                description: 'Your user permissions have been updated by an account administrator.',
                isReadS: "false",
                clickAction: "LOGOUT",
                owner: this.formFieldsRoute.cognitoSub,
                group: extractTenantGroup(this.formFieldsRoute.group),
                expirationTTL: Math.floor((new Date().getTime() + 90 * 24 * 60 * 60 * 1000) / 1000)              
            }
            await this.api(createNotification, {input} )
          }
          this.$router.go(-1)
        } catch(e) {
          console.error(e)
          if (e === false) {
            this.displayUserError("Please fill out all required fields.")
          } else {
            var errorMsg = e?.response?.data?.message || (typeof e?.response?.data === 'string' ? e.response.data : null) || e
            this.displayUserError(errorMsg)
          }
        }
        finally{
            this.loading = false
            this.showPopupUpdateEmail = false
        }
      },

        /** 
         * Create creates cognito user, adds user to tenant
         * group, adds user to DB */
        async createUser(){
            this.loading = true
            if (!this.group) {
                this.loading = false
                this.displayUserError("Unable to determine tenant group. Please refresh the page and try again.")
                return
            }
            try {
                await this.$refs.form.validate()  
                if (!this.validateReportingChain()) {
                    this.loading = false
                    return
                }
            }
            catch(e) {
                console.error(e)
                this.loading = false
                this.displayUserError("Please fill out all required fields and correct any errors.")
                return
            }

            // check for existing user
            try{
                var formattedEmail = this.cleanEmail(this.formFieldsRoute.email)
                var cognitoUser = await this.getCognitoUser(formattedEmail)
                if(cognitoUser.statusCode && cognitoUser.statusCode != 200 && cognitoUser.code != 'UserNotFoundException' ){
                    var error = {
                    code: cognitoUser.code,
                    message: cognitoUser.message
                    }
                    throw(error)
                }
                var enabled = cognitoUser.Enabled
                if(enabled){
                    throw{
                    errors: [
                        { message: "User already exists with provided email"}
                        ]
                    }
                }
            }
            catch(e) {
                console.error(e)
                
                // ignore user doesn't exist error in response
                if(e.errors){
                    this.displayUserError(e)
                    this.loading = false
                    return // only return if user exists
                }       
            }
            
            try{
                // Create DB record
                var input = {
                    ...this.formFieldsRoute,
                    userTenantId: this.tenantId,
                    role: '',
                    isCreator: false,
                    group: [
                        this.group
                    ]
                }   
                input.phone = cleanPhoneNumber(input.phone, 'US')
                if(input.phone ==='') delete input.phone
                const tenant = await this.api(getTenant, { id: input.userTenantId })
                if (!tenant.data.getTenant.users.items.length) {
                    if (!formattedEmail.startsWith('sysadmin-') && !formattedEmail.endsWith('@herasolutions.info')) {
                        input.isCreator = true
                    }
                }
                
                input.email = formattedEmail

                const notificationTypes = [
                    //HERA_MESSENGER_NEW_MESSAGE,
                    DAILY_ROSTER_IMPORT_COMPLETE,
                    DAILY_PERFORMANCE_DATA_IMPORT_COMPLETE,
                    WEEKLY_PERFORMANCE_DATA_IMPORT_COMPLETE,
                    TASK_REMINDER,
                    NEW_TASK,
                    EXPORT_READY_FOR_DOWNLOAD,
                    ASSOCIATE_MATCHING_SCREEN_LANDING,
                    PAYROLL_PROVIDER_MATCHES_FOUND,
                    DOCUMENT_SIGNATURE_ACKNOWLEDGMENT
                ]
                const notificationTypesForEmail = [
                    //HERA_MESSENGER_NEW_MESSAGE,
                    DAILY_ROSTER_IMPORT_COMPLETE,
                    DAILY_PERFORMANCE_DATA_IMPORT_COMPLETE,
                    WEEKLY_PERFORMANCE_DATA_IMPORT_COMPLETE,
                    TASK_REMINDER,
                    NEW_TASK,
                    EXPORT_READY_FOR_DOWNLOAD,
                    //VPL_HERA_AI_SUGGESTIONS
                    ASSOCIATE_MATCHING_SCREEN_LANDING,
                    PAYROLL_PROVIDER_MATCHES_FOUND,
                    DOCUMENT_SIGNATURE_ACKNOWLEDGMENT
                ]
                
                //Turn off all SMS notifications
                const notificationTypesBySMS = [
                    //HERA_MESSENGER_NEW_MESSAGE,
                    //DAILY_ROSTER_IMPORT_COMPLETE,
                    //DAILY_PERFORMANCE_DATA_IMPORT_COMPLETE,
                    //WEEKLY_PERFORMANCE_DATA_IMPORT_COMPLETE,
                    //TASK_REMINDER,
                    //NEW_TASK,
                    //CSV_EXPORT,
                    //VPL_HERA_AI_SUGGESTIONS
                    ASSOCIATE_MATCHING_SCREEN_LANDING,
                    PAYROLL_PROVIDER_MATCHES_FOUND
                ]
                const notificationTypesForBadgeCounter = [
                    HERA_MESSENGER_NEW_MESSAGE,
                    DAILY_ROSTER_IMPORT_COMPLETE,
                    DAILY_PERFORMANCE_DATA_IMPORT_COMPLETE,
                    WEEKLY_PERFORMANCE_DATA_IMPORT_COMPLETE,
                    TASK_REMINDER,
                    NEW_TASK,
                    EXPORT_READY_FOR_DOWNLOAD,
                    ASSOCIATE_MATCHING_SCREEN_LANDING,
                    PAYROLL_PROVIDER_MATCHES_FOUND,
                    DOCUMENT_SIGNATURE_ACKNOWLEDGMENT
                ]
                input.receiveNotificationInApp = notificationTypes
                input.receiveNotificationByEmail = notificationTypes
                input.receiveNotificationBySms = notificationTypesBySMS
                input.badgeCounterPreferences = notificationTypesForBadgeCounter
                
                // debugger
                const result = await this.api(createUser, {input: input})
                const userData = result;
                const userId = result.data.createUser.id

                let inputOwnerUser = {
                    id: tenant.data.getTenant.id,
                    ownerUserId: userId,
                }

                if(!tenant.data.getTenant.users.items.length){
                    await this.api(updateTenant, {input: inputOwnerUser})
                }

                try{
                    // create cognito user
                    cognitoUser = await this.createCognitoUser(formattedEmail, userId)
        
                    // Add user to group
                    var addToGroup = await this.addUserToCognitoGroup(formattedEmail, this.group)

                    if(this.formFieldsRoute.permissionFullAccess){
                        addToGroup = await this.addUserToCognitoGroup(formattedEmail, 'admin')
                    }
        
                    // get cognito user's sub
                    cognitoUser = await this.getCognitoUser(formattedEmail)
                    if(cognitoUser.statusCode && cognitoUser.statusCode != 200){
                        var error = {
                            code: cognitoUser.code,
                            message: cognitoUser.message
                        }
                        throw(error)
                    }
                    var sub = cognitoUser.UserAttributes.find((attr) => {
                        return attr.Name == "sub"
                    }).Value
                }
                catch(e){
                    if(userId){
                        await this.api(deleteUser, {input: {id: userId}})  
                    }
                    console.error(e)
                    this.loading = false
                    this.displayUserNotification({
                        title: "Error",
                        type: "error",
                        message: e
                    })
                    return
                }
                // update db record with cognito sub
                input = {
                    id: userId,
                    cognitoSub: sub,
                    group: [
                        this.group
                    ]
                }
                await this.api(updateUser, {input: input})

                // create zoho contact
                if(!this.$store.getters.isZohoCrmDisabled){
                    try{
                        const zohoCreateUserResponse = await this.createZohoContact(userData.data.createUser)
                        const input = {
                            id: userId,
                            group: [
                                this.group
                            ],
                            zohoCrmContactRecordId: zohoCreateUserResponse.data[0].details.id
                        }
                        const result = await this.api(updateUser, {input: input})
                    }catch(e){
                        console.error("ERROR: Zoho User Creation", e)
                    }
                }


                // Assign roles if any were selected
                if (this.assignedRoleIds.length > 0) {
                    try {
                        await setUserRoles(userId, this.assignedRoleIds)
                    } catch (e) {
                        console.error('Error assigning roles:', e)
                    }
                }

                //Clean up app state
                this.userModalOpen = false
                this.$emit('loadData')
                this.$message({
                    type: 'success',
                    message: 'User Created'
                });
                this.loading = false
                this.$router.go(-1)
            } 
            catch(e) {
                console.error(e)
                this.loading = false
                this.displayUserError(e)        
            }
        },

        /** 
         * Gets a single cognito user
         * @param {string} username 
         * */
        async getCognitoUser(username){
            let apiName = 'AdminQueries';
            let path = '/getUser';
            let myInit = {
                queryStringParameters: {
                "username": username,
                }, 
                headers: {
                'Content-Type' : 'application/json',
                Authorization: `${(await Auth.currentSession()).getAccessToken().getJwtToken()}`
                } 
            }
            var result = await API.get(apiName, path, myInit);
            return result
        },

        /** 
       * Create creates cognito user, adds user to tenant
       * group, adds user to DB */
      async updateUserAttributes(username, attributes){
        let apiName = 'AdminQueries';
        let path = '/updateUser';
        let post = {
            body: {
              "username": username,
              "attributes": attributes 
            }, 
            headers: {
              'Content-Type' : 'application/json',
              Authorization: `${(await Auth.currentSession()).getAccessToken().getJwtToken()}`
            } 
        }
        var result = await API.post(apiName, path, post);
        return result
      },


      /** 
       * Creates a new cognito user
       * @param {string} username 
       * */
      async createCognitoUser(username, userId, resend = false){
        let apiName = 'AdminQueries';
        let path = '/createUser';
        let post = {
            body: {
              "username": username,
              "userId": userId,
              "resend": resend,
              "tenantName": this.companyName
            }, 
            headers: {
              'Content-Type' : 'application/json',
              Authorization: `${(await Auth.currentSession()).getAccessToken().getJwtToken()}`
            } 
        }
        var result = await API.post(apiName, path, post);
        return result
      },

      /** 
       * Disables a cognito user
       * @param {string} username 
       * */
      async disableCognitoUser(username){
        let apiName = 'AdminQueries';
        let path = '/disableUser';
        let post = {
            body: {
              "username": username,
            }, 
            headers: {
              'Content-Type' : 'application/json',
              Authorization: `${(await Auth.currentSession()).getAccessToken().getJwtToken()}`
            } 
        }
        var result = await API.post(apiName, path, post);
        return result
      },

      /** 
       * Deletes a cognito user
       * @param {string} username 
       * */
      async deleteCognitoUser(username){
        let apiName = 'AdminQueries';
        let path = '/deleteUser';
        let post = {
            body: {
              "username": username,
            }, 
            headers: {
              'Content-Type' : 'application/json',
              Authorization: `${(await Auth.currentSession()).getAccessToken().getJwtToken()}`
            } 
        }
        var result = await API.post(apiName, path, post);
        return result
      },
    

    /**
       * Adds the user to a group
       * @param {string} username
       * @param {string} groupname
       */
      async addUserToCognitoGroup(username, groupname) { 
        let apiName = 'AdminQueries';
        let path = '/addUserToGroup';
        let myInit = {
            body: {
              "username" : username,
              "groupname": groupname
            }, 
            headers: {
              'Content-Type' : 'application/json',
              Authorization: `${(await Auth.currentSession()).getAccessToken().getJwtToken()}`
            } 
        }
        var result = await API.post(apiName, path, myInit);
      },

      /**
       * Removes the user to a group
       * @param {string} username
       * @param {string} groupname
       */
      async removeUserFromCognitoGroup(username, groupname) { 
        let apiName = 'AdminQueries';
        let path = '/removeUserFromGroup';
        let myInit = {
            body: {
              "username" : username,
              "groupname": groupname
            }, 
            headers: {
              'Content-Type' : 'application/json',
              Authorization: `${(await Auth.currentSession()).getAccessToken().getJwtToken()}`
            } 
        }
        var result = await API.post(apiName, path, myInit);
      },

      async updateZohoContact(user, contactStatus = "Active"){
        if(!user.zohoCrmContactRecordId) {
            const _result = await this.createZohoContact(user);
            return _result;
        }
        let _payload = {
            id: user.zohoCrmContactRecordId,
            Hera_User_ID: user.id,
            First_Name: user.firstName,
            Last_Name: user.lastName,
            Email: user.email,
            Phone: user?.phone,
            Account_Name: {
            id: user.tenant.zohoCrmAccountRecordId
            },
             Is_Owner: user.id === user.tenant.ownerUserId,
            Contact_Status: contactStatus
        }
        try {
            let _result = await updateZohoAccountUser('update', _payload, this.displayUserError)

            //update DSP Owner
            if (user.id === user.tenant.ownerUserId) {
                let _payloadTenant = {
                    id: user.tenant.zohoCrmAccountRecordId,
                    DSP_Owner: {
                        id: user.zohoCrmContactRecordId,
                    }
                }
                try {
                    await updateZohoAccountTenant('update', _payloadTenant, this.displayUserError)
                } 
                catch (error) {
                    console.error('error updating zoho account', error)
                }
            }
            return _result
        } catch (error) {
            console.error('error updating zoho user', error)
        }       
      },

      async createZohoContact(user, contactStatus = "Active"){
        let _payload = {
            Hera_User_ID: user.id,
            First_Name: user.firstName,
            Last_Name: user.lastName,
            Email: user.email,
            Phone: user?.phone,
            Account_Name: {
            id: user.tenant.zohoCrmAccountRecordId
            },
            Is_Owner: user.id === user.tenant.ownerUserId,
            Contact_Status: contactStatus
        }
        try {
          let _result = await updateZohoAccountUser('create', _payload, this.displayUserError)
          //update DSP Owner
          if (user.id === user.tenant.ownerUserId) {
                let _payloadTenant = {
                    id: user.tenant.zohoCrmAccountRecordId,
                    DSP_Owner: {
                        id: _result.data[0].details.id,
                    }
                }
                try {
                    await updateZohoAccountTenant('update', _payloadTenant, this.displayUserError)
                } 
                catch (error) {
                    console.error('error updating zoho account', error)
                }
            }
          return _result
        } catch (error) {
            console.error('error updating zoho user', error)
        }
      },
    },
}
</script>

<style lang="scss">
#show-popup-update-email {
    .el-dialog__body {
        padding-top: 0;
        padding-bottom: .5rem;
    }

    .el-dialog {
        margin-top: 33vh !important;
    }
}

</style>
